/**
 * useEntitlements — single source of truth for the user's subscription
 * tier, take rate, and feature flags. Backed by `/api/me/entitlements`.
 *
 * Gate any tier-locked UI behind:
 *   const { hasFeature } = useEntitlements();
 *   if (hasFeature('instant_payouts')) { … }
 *
 * Returns `loading=true` until the first fetch resolves; consumers should
 * usually render the locked state during that window (it's < 200ms).
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Feature key constants — keep aligned with backend/entitlements.py.
export const FEATURES = {
  INSTANT_PAYOUTS: 'instant_payouts',
  AI_SOAP: 'ai_soap',
  WHITE_LABEL_URL: 'white_label_url',
  TAX_PREP: 'tax_prep',
  PRIORITY_PLACEMENT: 'priority_placement',
  CARE_COORDINATOR: 'care_coordinator',
  PRACTICE_ANALYTICS: 'practice_analytics',
  MULTI_CLINICIAN_ROSTER: 'multi_clinician_roster',
  FEATURED_PROFILE: 'featured_profile',
  EARNINGS_ADVANCE: 'earnings_advance',
  VISIT_TRANSCRIPTS: 'visit_transcripts',
  FAMILY_LINK: 'family_link',
  VIDEO_CHECKINS: 'video_checkins',
  OUTCOME_ALERTS: 'outcome_alerts',
  PRIORITY_CONCIERGE: 'priority_concierge',
};

export function useEntitlements() {
  const { token } = useAuth();
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!token) { setEntitlements(null); setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/me/entitlements`,
        { headers: { Authorization: `Bearer ${token}` } });
      setEntitlements(data);
    } catch {
      setEntitlements(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  const hasFeature = useCallback((key) => {
    if (!entitlements) return false;
    return Array.isArray(entitlements.features) && entitlements.features.includes(key);
  }, [entitlements]);

  return { entitlements, loading, hasFeature, refresh };
}

export default useEntitlements;
